import React from "react";
import {
  Badge,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Button,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowClockwise16Regular,
  Sparkle16Regular,
  Add16Regular,
  ShieldError16Regular,
} from "@fluentui/react-icons";
import type { EmailInsight, SuggestedAction, Urgency } from "../insights";
import type { InsightState } from "../use-email-insight";

interface EmailInsightBannerProps {
  insight: EmailInsight | null;
  state: InsightState;
  /** Generic, non-leaking message when `state === "blocked"` (ADR-005). */
  blockedMessage?: string | null;
  /** Authored, user-facing explanation when `state === "error"`; falls back to
   * a generic string when absent. The hook supplies this only for `AiError`,
   * never for a raw internal error (A3/B3) — see `useEmailInsight`. */
  errorMessage?: string | null;
  /** Documented code for the same failure, shown so the user can quote or look
   * it up in `docs/error-codes.md`. */
  errorCode?: string | null;
  autoAnalyze: boolean;
  onAnalyze: () => void;
  onRefresh: () => void;
  onCreateTask?: () => void;
}

const ACTION_LABELS: Record<SuggestedAction, string> = {
  reply: "Reply",
  delegate: "Delegate",
  schedule: "Schedule",
  archive: "Archive",
  no_action: "No action",
};

const URGENCY_COLOR: Record<Urgency, "danger" | "informative" | "subtle"> = {
  high: "danger",
  normal: "informative",
  low: "subtle",
};

const containerStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  flexShrink: 0,
};

export function EmailInsightBanner({
  insight,
  state,
  blockedMessage,
  errorMessage,
  errorCode,
  autoAnalyze,
  onAnalyze,
  onRefresh,
  onCreateTask,
}: EmailInsightBannerProps) {
  if (state === "loading") {
    return (
      <div style={containerStyle}>
        <Spinner size="tiny" label="Analyzing…" />
      </div>
    );
  }

  if (state === "blocked") {
    return (
      <div style={containerStyle}>
        <MessageBar intent="warning">
          <MessageBarBody>{blockedMessage ?? "This email cannot be analyzed by the AI assistant."}</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div style={containerStyle}>
        <MessageBar intent="error">
          <MessageBarBody>
            {errorMessage ?? "Couldn't analyze this email."}
            {errorCode && (
              <>
                {" "}
                <Text
                  size={200}
                  font="monospace"
                  style={{ color: tokens.colorNeutralForeground3 }}
                >
                  ({errorCode})
                </Text>
              </>
            )}
          </MessageBarBody>
        </MessageBar>
        <Button
          size="small"
          appearance="subtle"
          icon={<ArrowClockwise16Regular />}
          onClick={onRefresh}
          aria-label="Refresh analysis"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!insight) {
    // Idle with no result: offer the manual entry point (auto is off or hasn't run).
    if (autoAnalyze) return null;
    return (
      <div style={containerStyle}>
        <Button size="small" appearance="subtle" icon={<Sparkle16Regular />} onClick={onAnalyze}>
          Analyze this email
        </Button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Badge appearance="filled" color={URGENCY_COLOR[insight.urgency]}>
          {insight.urgency}
        </Badge>
        {insight.phishingSuspected && (
          <Badge
            appearance="filled"
            color="danger"
            icon={<ShieldError16Regular />}
            aria-label="Possible phishing"
          >
            Possible phishing
          </Badge>
        )}
        <Text size={300} weight="semibold" style={{ flex: 1 }}>
          {insight.intent}
        </Text>
        {onCreateTask && (
          <Button
            size="small"
            appearance="subtle"
            icon={<Add16Regular />}
            onClick={onCreateTask}
            aria-label="Create task"
          />
        )}
        <Button
          size="small"
          appearance="subtle"
          icon={<ArrowClockwise16Regular />}
          onClick={onRefresh}
          aria-label="Refresh analysis"
        />
      </div>

      {insight.actions.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {insight.actions.map((a) => (
            <Badge key={a} appearance="tint" color="brand">
              {ACTION_LABELS[a]}
            </Badge>
          ))}
        </div>
      )}

      {insight.asks.length > 0 && (
        <ul style={{ margin: 0, paddingInlineStart: 18 }}>
          {insight.asks.map((ask, i) => (
            <li key={i}>
              <Text size={200}>{ask}</Text>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
